import { createFileRoute, Link, useNavigate, retainSearchParams } from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listManifests, createManifest, closeManifest } from "@/lib/manifests.functions";
import { listPickups, schedulePickup, cancelPickup } from "@/lib/pickups.functions";
import { runServerFn } from "@/lib/server-fn";
import { useScope } from "@/hooks/use-scope";
import { PageHeader } from "@/components/ui/page-header";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Truck, X, CheckCircle2, Printer } from "lucide-react";
import { toast } from "sonner";
import { DateRangeFilter } from "@/components/filters/date-range-filter";
import { SortDirectionToggle } from "@/components/filters/sort-direction-toggle";
import { dateRangeSearchSchema, retainDateRangeKeys } from "@/lib/date-range-search";

export const Route = createFileRoute("/_authenticated/app/admin/manifests")({
  validateSearch: zodValidator(dateRangeSearchSchema),
  search: { middlewares: [retainSearchParams([...retainDateRangeKeys])] },
  component: ManifestsAdmin,
});

function ManifestsAdmin() {
  const { tenantId, facilityId } = useScope();
  const qc = useQueryClient();
  const urlSearch = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const sort = urlSearch.sort ?? "desc";
  const lm = useServerFn(listManifests);
  const cm = useServerFn(createManifest);
  const close = useServerFn(closeManifest);
  const lp = useServerFn(listPickups);
  const sp = useServerFn(schedulePickup);
  const cp = useServerFn(cancelPickup);

  const [carrier, setCarrier] = useState("ups");
  const [pickupDate, setPickupDate] = useState(new Date().toISOString().slice(0, 10));
  const [readyTime, setReadyTime] = useState("14:00");
  const [closeTime, setCloseTime] = useState("17:00");

  const enabled = !!tenantId && !!facilityId;

  const { data: mans } = useQuery({
    queryKey: ["admin-manifests", tenantId, facilityId, urlSearch.start, urlSearch.end, sort],
    enabled,
    queryFn: () => lm({ data: {
      tenantId: tenantId!, facilityId: facilityId!,
      ...(urlSearch.start || urlSearch.end
        ? { fromDate: urlSearch.start, toDate: urlSearch.end }
        : { days: 30 }),
      sort,
    } }),
  });
  const { data: picks } = useQuery({
    queryKey: ["admin-pickups", tenantId, facilityId],
    enabled,
    queryFn: () => lp({ data: { tenantId: tenantId!, facilityId: facilityId!, days: 14 } }),
  });

  const onCreateManifest = async () => {
    if (!enabled) return;
    await runServerFn(() => cm({ data: { tenantId: tenantId!, facilityId: facilityId!, carrier } }), "Create manifest");
    toast.success("Manifest created");
    qc.invalidateQueries({ queryKey: ["admin-manifests"] });
  };
  const onClose = async (id: string) => {
    if (!confirm("Close this manifest? This finalizes end-of-day handoff.")) return;
    const res = await runServerFn(() => close({ data: { manifestId: id } }), "Close manifest");
    toast.success(`Closed · ${res.shipments} shipments`);
    qc.invalidateQueries({ queryKey: ["admin-manifests"] });
  };
  const onSchedule = async () => {
    if (!enabled) return;
    await runServerFn(() => sp({ data: { tenantId: tenantId!, facilityId: facilityId!, carrier, pickupDate, readyTime, closeTime } }), "Schedule pickup");
    toast.success("Pickup scheduled");
    qc.invalidateQueries({ queryKey: ["admin-pickups"] });
  };
  const onCancelPickup = async (id: string) => {
    if (!confirm("Cancel this pickup?")) return;
    await runServerFn(() => cp({ data: { id } }), "Cancel pickup");
    qc.invalidateQueries({ queryKey: ["admin-pickups"] });
  };

  return (
    <>
      <PageHeader title="Manifests & Pickups" description="End-of-day carrier manifests and scheduled pickups." />
      <div className="px-4 sm:px-6 py-4 grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-base">Schedule Pickup</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-xs">Carrier</Label><Input value={carrier} onChange={(e) => setCarrier(e.target.value.toLowerCase())} /></div>
              <div><Label className="text-xs">Date</Label><Input type="date" value={pickupDate} onChange={(e) => setPickupDate(e.target.value)} /></div>
              <div><Label className="text-xs">Ready</Label><Input type="time" value={readyTime} onChange={(e) => setReadyTime(e.target.value)} /></div>
              <div><Label className="text-xs">Close</Label><Input type="time" value={closeTime} onChange={(e) => setCloseTime(e.target.value)} /></div>
            </div>
            <div className="flex gap-2">
              <Button onClick={onSchedule} disabled={!enabled}>Schedule pickup</Button>
              <Button variant="outline" onClick={onCreateManifest} disabled={!enabled}>New manifest ({carrier})</Button>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-base">Upcoming Pickups</CardTitle></CardHeader>
          <CardContent className="p-0">
            {(picks?.pickups ?? []).length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No pickups scheduled.</div>
            ) : (
              <ul className="divide-y">
                {picks!.pickups.map((p: any) => (
                  <li key={p.id} className="p-3 flex items-center justify-between gap-3 text-sm">
                    <div className="flex items-center gap-3 min-w-0">
                      <Truck className="h-4 w-4 text-muted-foreground" />
                      <div>
                        <div className="font-medium">{p.carrier.toUpperCase()} · {p.pickup_date}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.ready_time ?? "—"} – {p.close_time ?? "—"}{p.confirmation ? ` · #${p.confirmation}` : ""}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{p.status}</Badge>
                      {p.status === "scheduled" && (
                        <Button size="sm" variant="ghost" onClick={() => onCancelPickup(p.id)}><X className="h-4 w-4" /></Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="px-4 sm:px-6 pb-6">
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <CardTitle className="text-base">Recent Manifests</CardTitle>
              <div className="flex items-center gap-2">
                <DateRangeFilter
                  value={{ start: urlSearch.start, end: urlSearch.end }}
                  onChange={(r) => navigate({ search: (p: typeof urlSearch) => ({ ...p, start: r.start, end: r.end }) })}
                />
                <SortDirectionToggle
                  value={sort}
                  onChange={(s) => navigate({ search: (p: typeof urlSearch) => ({ ...p, sort: s === "desc" ? undefined : s }) })}
                />
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {(mans?.manifests ?? []).length === 0 ? (
              <div className="p-6 text-sm text-center text-muted-foreground">No manifests yet.</div>
            ) : (
              <ul className="divide-y">
                {mans!.manifests.map((m: any) => (
                  <li key={m.id} className="p-3 flex items-center justify-between gap-3 text-sm">
                    <div>
                      <div className="font-medium">{m.carrier.toUpperCase()} · {m.manifest_date}</div>
                      <div className="text-xs text-muted-foreground font-mono">{m.manifest_number ?? m.id.slice(0, 8)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline">{m.status}</Badge>
                      {m.document_url && (
                        <a href={m.document_url} target="_blank" rel="noreferrer" className="text-xs underline">PDF</a>
                      )}
                      <Button asChild size="sm" variant="ghost">
                        <Link to="/app/manifests/$manifestId/print" params={{ manifestId: m.id }} target="_blank">
                          <Printer className="h-4 w-4 mr-1" />Print
                        </Link>
                      </Button>
                      {m.status === "open" && (
                        <Button size="sm" variant="outline" onClick={() => onClose(m.id)}>
                          <CheckCircle2 className="h-4 w-4 mr-1" />Close
                        </Button>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}